/**
 * Notifications route — Timeline of system notifications.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { Bell, Check, CheckCheck, Trash2, AlertTriangle, Info, AlertCircle, CheckCircle, Wifi, WifiOff } from "lucide-react";
import { useNotificationsStream, useEventStreamStatus } from "../hooks/useEventStream";
import { CorpoCard } from "../components/ui/CorpoCard";
import { CRTHeader } from "../components/ui/CRTHeader";
import { GlyphButton } from "../components/ui/GlyphButton";
import { HexBadge } from "../components/ui/HexBadge";

interface Notification {
  id: number;
  type: string;
  title: string;
  message: string | null;
  severity: "info" | "warn" | "error" | "success";
  createdAt: number;
  readAt: number | null;
}

interface NotificationsResponse {
  notifications: Notification[];
  unreadCount: number;
}

function formatDate(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getSeverityIcon(severity: Notification["severity"]) {
  switch (severity) {
    case "error":
      return <AlertCircle size={16} className="text-[var(--bw-danger)]" />;
    case "warn":
      return <AlertTriangle size={16} className="text-[var(--bw-warn)]" />;
    case "success":
      return <CheckCircle size={16} className="text-[var(--bw-success)]" />;
    default:
      return <Info size={16} className="text-[var(--bw-accent)]" />;
  }
}

function getSeverityBadge(severity: Notification["severity"]): "danger" | "warning" | "success" | "accent" {
  switch (severity) {
    case "error":
      return "danger";
    case "warn":
      return "warning";
    case "success":
      return "success";
    default:
      return "accent";
  }
}

export function NotificationsRoute() {
  const queryClient = useQueryClient();

  // Live notification stream
  const { latestNotification, isConnected: wsConnected } = useNotificationsStream();
  const { state: wsState } = useEventStreamStatus();

  // Refetch when we receive a new notification via WebSocket
  useEffect(() => {
    if (latestNotification) {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    }
  }, [latestNotification, queryClient]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => {
      const res = await fetch("/api/notifications");
      return res.json() as Promise<NotificationsResponse>;
    },
  });

  // Mark as read mutation
  const markReadMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/notifications/${id}/read`, { method: "POST" });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  // Mark all as read mutation
  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/notifications/read-all", { method: "POST" });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/notifications/${id}`, { method: "DELETE" });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const notifications = data?.notifications ?? [];
  const unreadCount = data?.unreadCount ?? 0;

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <CRTHeader text="Notificaciones" level={2} />
          {unreadCount > 0 && (
            <HexBadge label={`${unreadCount} SIN LEER`} variant="accent" />
          )}
          {wsConnected ? (
            <div className="flex items-center gap-1">
              <Wifi size={12} className="text-[var(--bw-success)]" />
              <span className="text-xs text-[var(--bw-success)]">LIVE</span>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <WifiOff size={12} className="text-[var(--bw-muted)]" />
              <span className="text-xs text-[var(--bw-muted)]">
                {wsState === "connecting" || wsState === "reconnecting" ? "..." : "OFFLINE"}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <GlyphButton
              variant="secondary"
              size="sm"
              onClick={() => markAllReadMutation.mutate()}
              icon={<CheckCheck size={14} />}
            >
              Marcar todo
            </GlyphButton>
          )}
          <GlyphButton
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
          >
            Actualizar
          </GlyphButton>
        </div>
      </div>

      {isLoading && (
        <div className="text-[var(--bw-muted)] animate-pulse">
          ESTABLECIENDO CONEXIÓN...
        </div>
      )}

      {error && (
        <CorpoCard className="border-[var(--bw-danger)]">
          <p className="text-[var(--bw-danger)]">
            ⚠ ERROR: {error instanceof Error ? error.message : "Unknown"}
          </p>
        </CorpoCard>
      )}

      {!isLoading && notifications.length === 0 && (
        <CorpoCard>
          <div className="text-center py-12">
            <div className="text-4xl text-[var(--bw-muted)] mb-4">
              ◇ ◆ ◇
            </div>
            <p className="text-xl text-[var(--bw-muted)] uppercase tracking-widest mb-2">
              NO TRANSMISSIONS
            </p>
            <p className="text-sm text-[var(--bw-muted)] uppercase tracking-wider">
              SIGNAL CLEAR
            </p>
          </div>
        </CorpoCard>
      )}

      {notifications.length > 0 && (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 top-0 bottom-0 w-px bg-[var(--bw-border)]" />

          {/* Notifications */}
          <div className="space-y-4">
            {notifications.map((notif) => (
              <div key={notif.id} className="relative pl-10">
                {/* Timeline dot */}
                <div
                  className={`
                    absolute left-2.5 top-4 w-3 h-3 rounded-full border-2
                    ${notif.readAt
                      ? "bg-[var(--bw-surface)] border-[var(--bw-border)]"
                      : "bg-[var(--bw-accent)] border-[var(--bw-accent)]"
                    }
                  `}
                />

                <CorpoCard
                  className={`
                    ${!notif.readAt ? "border-l-2 border-l-[var(--bw-accent)]" : ""}
                  `}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      {getSeverityIcon(notif.severity)}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="font-bold text-sm text-[var(--bw-fg)] break-words">
                            {notif.title}
                          </span>
                          <HexBadge
                            label={notif.type}
                            variant={getSeverityBadge(notif.severity)}
                            size="sm"
                          />
                        </div>
                        {notif.message && (
                          <p className="text-sm text-[var(--bw-muted)] break-words">
                            {notif.message}
                          </p>
                        )}
                        <p className="text-xs text-[var(--bw-muted)] mt-2">
                          {formatDate(notif.createdAt)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1">
                      {!notif.readAt && (
                        <button
                          onClick={() => markReadMutation.mutate(notif.id)}
                          className="
                            p-1.5 rounded
                            text-[var(--bw-muted)] hover:text-[var(--bw-accent)]
                            hover:bg-[var(--bw-surface-2)]
                            transition-colors
                          "
                          title="Marcar como leída"
                        >
                          <Check size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => deleteMutation.mutate(notif.id)}
                        className="
                          p-1.5 rounded
                          text-[var(--bw-muted)] hover:text-[var(--bw-danger)]
                          hover:bg-[var(--bw-danger)]/10
                          transition-colors
                        "
                        title="Eliminar"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </CorpoCard>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
